/**
 * storyCanvasMappers — pure functions mapping REST API shapes → view-models.
 *
 * Source types : CanvasWidgetApi (snake_case, item_id key)
 * Target types : StoryWidget    (camelCase, id key) — same view-model the WS
 *                                canvas consumes, so the canvas component needs
 *                                no changes regardless of which transport delivers data.
 *
 * Chart widgets require a second step: after the widget is mapped, call
 * injectChartDataset() to populate the Vega-Lite spec's data.values with rows
 * fetched from the dataset endpoint.
 */
import type { VisualizationSpec } from "vega-embed";
import type {
  ChartWidgetApi,
  EmbedWidgetApi,
  ImageWidgetApi,
  InsightWidgetApi,
  KpiWidgetApi,
  LinkWidgetApi,
  NarrativeWidgetApi,
  ReportWidgetApi,
  SummaryWidgetApi,
  TextWidgetApi,
  CanvasSearchResultApi,
  CanvasWidgetApi,
  CanvasItemStubApi,
  VisibilityMap,
} from "./storyCanvas.types";
import { STORY_VISIBILITY_ARTIFACT } from "./storyCanvas.types";
import { toCanvasPosition } from "./storyGridUnits";
import type {
  ChartWidget,
  EmbedContentWidget,
  ImageContentWidget,
  InsightWidget,
  KpiWidget,
  LinkContentWidget,
  NarrativeWidget,
  ReportWidget,
  SummaryWidget,
  TextContentFormatting,
  TextContentWidget,
  StoryWidget,
  WidgetPosition,
} from "../dataStories/generateStory.types";
import { toEmbedPlatform } from "../kg/detectEmbedPlatform";
import type { ReportData } from "../../components/molecules/DragDropReportWidget/DragDropReportWidget.types";

/**
 * Fallback thumbnail for canvas-search results — the backend's `thumbnail_url`
 * is always null today (not yet populated). Reuses the existing "Add reports"
 * chart icon (DragDropReportWidget/StoryWidget) rather than introducing a new
 * placeholder asset.
 */
export const CANVAS_SEARCH_PLACEHOLDER_THUMBNAIL = "/bar-chart-big.png";

// ─── Visibility helpers ────────────────────────────────────────────────────────

/**
 * Resolve Story-dashboard companion flags from live `visibility`.
 *
 * Supports both shapes:
 * - Flat (live GET): `{ is_summary_visible, is_narrative_visible }`
 * - Nested: `{ story: { is_summary_visible, … } }`
 *
 * `null` / missing → false (chart stays full-width until Insert).
 */
export function resolveStoryVisibilityFlags(
  visibility?: VisibilityMap | null,
): {
  isSummaryVisible: boolean;
  isNarrativeVisible: boolean;
} {
  if (!visibility) {
    return { isSummaryVisible: false, isNarrativeVisible: false };
  }

  const flatSummary = visibility.is_summary_visible;
  const flatNarrative = visibility.is_narrative_visible;
  const hasFlatKeys =
    typeof flatSummary === "boolean" || typeof flatNarrative === "boolean";

  if (hasFlatKeys) {
    return {
      isSummaryVisible: flatSummary === true,
      isNarrativeVisible: flatNarrative === true,
    };
  }

  const nested = visibility[STORY_VISIBILITY_ARTIFACT];
  const storyFlags =
    nested && typeof nested === "object"
      ? (nested as Record<string, unknown>)
      : undefined;
  return {
    isSummaryVisible: storyFlags?.is_summary_visible === true,
    isNarrativeVisible: storyFlags?.is_narrative_visible === true,
  };
}

/**
 * Preserve explicit API `null` (needs accept/reject). Only omit when the field
 * is absent (`undefined`) so widgets without a status lifecycle stay clean.
 */
export function resolveCanvasItemStatus(
  status: string | null | undefined,
): string | null | undefined {
  return status === undefined ? undefined : status;
}

/**
 * Resolve the manifest-stub position used for canvas placement, on the
 * canvas grid (storyGridUnits maps the stored x / w).
 *
 * Browser-captured widgets may arrive with `position: null` for first-time
 * placement. After the canvas auto-places and PATCHes them, the backend returns
 * a real position and that persisted geometry must become source of truth too.
 */
export function resolveCanvasItemPosition(
  stub: Pick<CanvasItemStubApi, "type" | "position">,
): WidgetPosition | null | undefined {
  return stub.position ? toCanvasPosition(stub.position) : stub.position;
}

// ─── Per-type mappers ──────────────────────────────────────────────────────────

function mapKpi(api: KpiWidgetApi): KpiWidget {
  return {
    id: api.item_id,
    type: "kpi",
    status: resolveCanvasItemStatus(api.status),
    name: api.name,
    value: api.value,
    delta: api.delta ?? undefined,
    deltaPct: api.delta_pct ?? undefined,
    trend: api.trend ?? undefined,
    comparisonLabel: api.comparison_label ?? undefined,
    footerSource: api.footer_source
      ? {
          name: api.footer_source.name,
          logoSrc: api.footer_source.logo_src ?? undefined,
          href: api.footer_source.href ?? undefined,
        }
      : undefined,
    description: api.description ?? undefined,
  };
}

function mapNarrative(api: NarrativeWidgetApi): NarrativeWidget {
  return {
    id: api.item_id,
    type: "narrative",
    title: api.title ?? undefined,
    body: api.body,
    // REST schema does not carry accent — defaults to false (plain border)
    accent: false,
    status: resolveCanvasItemStatus(api.status),
  };
}

function mapSummary(api: SummaryWidgetApi): SummaryWidget {
  return {
    id: api.item_id,
    type: "summary",
    title: api.title ?? undefined,
    body: api.body,
    // REST schema does not carry accent — defaults to false (plain border)
    accent: false,
    status: resolveCanvasItemStatus(api.status),
  };
}

/**
 * Maps the chart widget metadata only — spec has empty data.values at this point.
 * Call injectChartDataset() after fetching the dataset to complete the chart.
 */
function mapChart(api: ChartWidgetApi): ChartWidget {
  const { isSummaryVisible, isNarrativeVisible } = resolveStoryVisibilityFlags(
    api.visibility,
  );
  return {
    id: api.item_id,
    type: "chart",
    status: resolveCanvasItemStatus(api.status),
    title: api.title,
    spec: api.spec,
    narrative: api.narrative ?? undefined,
    aiSummary: api.ai_summary
      ? {
          title: api.ai_summary.title,
          body: api.ai_summary.body,
          ...(api.ai_summary.sections?.length
            ? {
                sections: api.ai_summary.sections.map((s) => ({
                  id: s.id,
                  kind: s.kind,
                  title: s.title,
                  body: s.body,
                  bullets: s.bullets,
                  collapsible: s.collapsible,
                })),
              }
            : {}),
        }
      : undefined,
    isSummaryVisible,
    isNarrativeVisible,
    ...(api.modes?.length
      ? { modes: api.modes.map((m) => ({ id: m.id, label: m.label })) }
      : {}),
    ...(api.active_mode_id ? { activeModeId: api.active_mode_id } : {}),
    ...(api.footer_chips?.length
      ? {
          footerChips: api.footer_chips.map((c) => ({
            id: c.id,
            label: c.label,
          })),
        }
      : {}),
    ...(api.footer_more_count != null
      ? { footerMoreCount: api.footer_more_count }
      : {}),
    ...(api.footer_source
      ? {
          footerSource: {
            name: api.footer_source.name,
            logoSrc: api.footer_source.logo_src ?? undefined,
            href: api.footer_source.href ?? undefined,
          },
        }
      : {}),
  };
}

const SOURCE_LABELS: Record<string, string> = {
  tableau: "Tableau",
  powerbi: "Power BI",
  salesforce: "Salesforce",
  hubspot: "HubSpot",
  looker: "Looker",
  metabase: "Metabase",
};

/** A connector key's display name: known tools by name, otherwise the key's words in title case. */
export function sourceLabel(key: string): string {
  const known = SOURCE_LABELS[key.trim().toLowerCase().replace(/[\s_-]+/g, "")];
  if (known) return known;
  return key
    .split(/[_-]+/)
    .filter(Boolean)
    .map((w) => `${w.charAt(0).toUpperCase()}${w.slice(1)}`)
    .join(" ");
}

function mapReport(api: ReportWidgetApi): ReportWidget {
  const source =
    typeof api.source === "string"
      ? { id: api.source, name: sourceLabel(api.source) }
      : {
          id: api.source.id,
          name: api.source.name,
          logoUrl: api.source.logo_url ?? undefined,
        };
  return {
    id: api.item_id,
    type: api.type,
    status: resolveCanvasItemStatus(api.status),
    title: api.title,
    source,
    connectorAssetId: api.connector_asset_id,
    connectionId: api.connection_id,
    embedUrl: api.embed_url,
    thumbnailUrl: api.thumbnail_url ?? undefined,
    kgEntityId: api.kg_entity_id ?? undefined,
    visualId: api.embed_config?.visual_id ?? undefined,
    visualTitle: api.embed_config?.visual_title ?? undefined,
    pageName: api.embed_config?.page_id ?? undefined,
    embedConfig: api.embed_config
      ? {
          reportId: api.embed_config.report_id ?? undefined,
          groupId: api.embed_config.group_id ?? undefined,
          pageId: api.embed_config.page_id ?? undefined,
          visualId: api.embed_config.visual_id ?? undefined,
          dashboardId: api.embed_config.dashboard_id ?? undefined,
          tileId: api.embed_config.tile_id ?? undefined,
        }
      : undefined,
    salesforceObjectType: api.salesforce_object_type ?? undefined,
    salesforceId: api.salesforce_id ?? undefined,
    instanceUrl: api.instance_url ?? undefined,
    url: api.url ?? undefined,
    toolFilters: api.tool_filters?.length
      ? api.tool_filters.map((f) => ({
          index: f.index,
          field: f.field,
          values: f.values,
          ...(f.exclude !== undefined ? { exclude: f.exclude } : {}),
        }))
      : undefined,
    snapshotSvg: api.snapshot_svg ?? undefined,
    hubspotQuery: api.hubspot_query ?? undefined,
  };
}

function mapLink(api: LinkWidgetApi): LinkContentWidget {
  return {
    id: api.item_id,
    type: "link",
    status: resolveCanvasItemStatus(api.status),
    title: api.metadata?.title ?? undefined,
    content: api.content,
  };
}

function mapEmbed(api: EmbedWidgetApi): EmbedContentWidget {
  return {
    id: api.item_id,
    type: "embed",
    status: resolveCanvasItemStatus(api.status),
    content: api.content,
  };
}

function mapImage(api: ImageWidgetApi): ImageContentWidget {
  return {
    id: api.item_id,
    type: "image",
    status: resolveCanvasItemStatus(api.status),
    content: api.content,
    altText: api.metadata?.alt_text ?? undefined,
  };
}

function mapTextFormatting(
  formatting: TextWidgetApi["metadata"]["formatting"],
): TextContentFormatting | undefined {
  if (!formatting) return undefined;
  return {
    fontFamily: formatting.font_family ?? undefined,
    fontSize: formatting.font_size ?? undefined,
    color: formatting.color ?? undefined,
    bold: formatting.bold ?? undefined,
    italic: formatting.italic ?? undefined,
    underline: formatting.underline ?? undefined,
    alignment: formatting.alignment ?? undefined,
    highlight: formatting.highlight ?? undefined,
    highlightColor: formatting.highlight_color ?? undefined,
  };
}

function mapText(api: TextWidgetApi): TextContentWidget {
  return {
    id: api.item_id,
    type: "text",
    status: resolveCanvasItemStatus(api.status),
    content: api.content,
    formatting: mapTextFormatting(api.metadata?.formatting),
  };
}

function mapInsight(api: InsightWidgetApi): InsightWidget {
  return {
    id: api.item_id,
    type: "insight",
    bullets: Array.isArray(api.bullets) ? api.bullets : [],
    sections: Array.isArray(api.sections)
      ? api.sections.map((s) => ({
          id: s.id,
          kind: s.kind,
          title: s.title,
          body: s.body,
          bullets: s.bullets,
          collapsible: s.collapsible,
        }))
      : undefined,
  };
}

// ─── Dispatch ─────────────────────────────────────────────────────────────────

/**
 * Map a single REST widget response to its view-model.
 * For chart widgets the returned spec has empty data — call injectChartDataset()
 * once the dataset rows are available.
 *
 * @throws if widget.type is not a known canvas item type.
 */
export function mapCanvasWidget(api: CanvasWidgetApi): StoryWidget {
  const mapped = mapCanvasWidgetOrNull(api);
  if (!mapped) {
    const unknown = api as { type?: string };
    throw new Error(`mapCanvasWidget: unknown widget type "${unknown.type}"`);
  }
  return mapped;
}

/**
 * mapCanvasWidget() for data read off the server: null, not a throw, for a type
 * it has no view-model for — a Chart Studio `chart_2` / `kpi_2` (drawn from its
 * canvas item's compiled spec instead) or a type added after this build — so one
 * such item never takes down the page that lists it.
 */
export function mapCanvasWidgetOrNull(api: CanvasWidgetApi): StoryWidget | null {
  switch (api.type) {
    case "kpi":
      return mapKpi(api);
    case "narrative":
      return mapNarrative(api);
    case "summary":
      return mapSummary(api);
    case "chart":
      return mapChart(api);
    case "insight":
      return mapInsight(api);
    case "report":
    case "ingested_widget":
    case "captured_widget":
      return mapReport(api);
    case "link":
      return mapLink(api);
    case "embed":
      return mapEmbed(api);
    case "image":
      return mapImage(api);
    case "text":
      return mapText(api);
    default:
      return null;
  }
}

// ─── Chart dataset injection ───────────────────────────────────────────────────

/**
 * Inject dataset rows into a mapped ChartWidget's Vega-Lite spec.
 *
 * The backend stores the spec without row data (data.values is empty or absent).
 * After fetching GET .../canvas/datasets/:canvas_item_id, pass the rows here to
 * produce a renderable spec.
 *
 * Returns a new ChartWidget — does not mutate the input.
 */
export function injectChartDataset(
  widget: ChartWidget,
  rows: Record<string, unknown>[],
): ChartWidget {
  const spec = widget.spec as Record<string, unknown>;
  return {
    ...widget,
    spec: {
      ...spec,
      data: { values: rows },
    } as VisualizationSpec,
  };
}

// ─── Manifest stub → skeleton placeholder ─────────────────────────────────────

/**
 * Build a minimal skeleton view-model from a manifest item stub so the canvas
 * can render a loading placeholder in the correct grid position while the full
 * widget fetch is in flight.
 *
 * Mirrors buildPlaceholderWidget() in generateStoryMappers but sources its input
 * from the REST manifest (CanvasItemStubApi) rather than a WS plan event.
 */
export function buildManifestPlaceholder(stub: CanvasItemStubApi): StoryWidget {
  // A Chart Studio item draws in the same tile as its legacy type.
  const rowType = stub.type as string;
  if (rowType === "chart_2" || rowType === "kpi_2") {
    return buildManifestPlaceholder({ ...stub, type: rowType === "chart_2" ? "chart" : "kpi" });
  }
  switch (stub.type) {
    case "kpi":
      return {
        id: stub.item_id,
        type: "kpi",
        status: resolveCanvasItemStatus(stub.status),
        name: "",
        value: "",
      };
    case "narrative":
      return {
        id: stub.item_id,
        type: "narrative",
        body: "",
        accent: false,
        status: resolveCanvasItemStatus(stub.status),
      };
    case "summary":
      return {
        id: stub.item_id,
        type: "summary",
        body: "",
        accent: false,
        status: resolveCanvasItemStatus(stub.status),
      };
    case "chart":
      return {
        id: stub.item_id,
        type: "chart",
        status: resolveCanvasItemStatus(stub.status),
        title: "",
        spec: {} as VisualizationSpec,
      };
    case "insight":
      return { id: stub.item_id, type: "insight", bullets: [] };
    case "report":
    case "ingested_widget":
    case "captured_widget":
      return {
        id: stub.item_id,
        type: stub.type,
        status: resolveCanvasItemStatus(stub.status),
        title: "",
        connectorAssetId: "",
        connectionId: null,
        embedUrl: null,
      };
    case "link":
      return { id: stub.item_id, type: "link", content: "" };
    case "embed":
      return { id: stub.item_id, type: "embed", content: "" };
    case "image":
      return { id: stub.item_id, type: "image", content: "" };
    case "text":
      return { id: stub.item_id, type: "text", content: "" };
    default:
      // A type added after this build: an empty tile, not a crashed page.
      return { id: stub.item_id, type: "text", content: "" };
  }
}

// ─── Canvas search result → ReportData ────────────────────────────────────────

/**
 * Map one canvas-search API result to the ReportData view-model that
 * DragDropReportWidget's results dropdown already renders — reused as-is
 * rather than introducing a parallel search-result type.
 */
// TODO:if it is tableau need only vis name and connector_id
// if powerBi need vis name embed url connector_id and assest id
export function mapCanvasSearchResult(api: CanvasSearchResultApi): ReportData {
  return {
    id: api.result_id,
    name: api.title,
    type: api.type,
    source: api.source,
    platform: toEmbedPlatform(api.source),
    connectorAssetId: api.connector_asset_id ?? undefined,
    canvasItemId: api.canvas_item_id ?? undefined,
    connectionId: api.connection_id ?? undefined,
    embedUrl: api.embed_url ?? null,
    description: api.story_title ?? undefined,
    thumbnailSrc: api.thumbnail_url ?? CANVAS_SEARCH_PLACEHOLDER_THUMBNAIL,
  };
}
